/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch } from 'react-redux';
import { callApi } from 'api/helpers/api.helpers';
import ePCTApi from '../../api/ePCT.api';
import { pctCallbackTimeout } from '../../settings';
import { startApplicationLoading, stopApplicationLoading } from '../../redux/features/application/application.slice';
import { RoutePath } from '../../shared/enums/RoutePath';

export default function useEPCTIframe({ procedure, initialSubmissionProcedure}) {
  const dispatch = useDispatch();
  const [epctURL, setEpctURL] = useState(null);
  const [loadingError, setLoadingError] = useState(false);
  const [oid, setoid] = useState(null);
  const history = useHistory();
  const editMatchRoute = useRouteMatch('/:procedure/:initialSubmissionProcedure?/epct/edit/:submissionId');
  const createMatchRoute = useRouteMatch('/:procedure/:initialSubmissionProcedure?/epct/online-filing');
  const { submissionId } = useParams();

  const getAuthToken = async () => {
    const authToken = await callApi(ePCTApi.getAuthToken, { onError: () => setLoadingError(true) })(procedure);
    return authToken;
  };

  useEffect(() => {
    const handler = (event) => {
      if (event.data === 'closed') {
        dispatch(startApplicationLoading());
        setTimeout(() => {
          dispatch(stopApplicationLoading());
          history.push(
            RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
              procedure,
              initialSubmissionProcedure
            }),
          );
        }, pctCallbackTimeout);
      }
    };
    window.addEventListener('message', handler);
    (async function renderEpctSection() {
      const authToken = await getAuthToken();
      if (editMatchRoute) {
        dispatch(startApplicationLoading());
        await callApi(ePCTApi.editEPCT, {
          onSuccess: (data) => {
            const urlParams = new URLSearchParams(data.url);
            const oidParam = urlParams.get('oid');
            setoid(oidParam);
            setEpctURL(data.url);
            dispatch(stopApplicationLoading());
          },
          onError: () => {
            setLoadingError(true);
          },
        })(authToken, submissionId, procedure);
      }

      if (createMatchRoute) {
        const {
          RO,
          fileReference,
          requestLanguage,
          dataProtectionAcceptance,
        } = history.location.state.epctCreationData;

        const epctParams = {
          interfaceLanguage: requestLanguage,
          proceduralLanguage: requestLanguage,
          receivingOffice: RO,
          fileReference,
          authToken,
          procedure,
          initialSubmissionProcedure,
          ...(dataProtectionAcceptance && { dataProtectionAcceptance }),
        };

        await callApi(ePCTApi.createEPCT, {
          onSuccess: (data) => {
            const urlParams = new URLSearchParams(data.url);
            const oidParam = urlParams.get('oid');
            setoid(oidParam);
            setEpctURL(data.url);
            dispatch(stopApplicationLoading());
          },
          onError: () => {
            setLoadingError(true);
          },
        })(epctParams);
      }
    })();

    return () => window.removeEventListener('message', handler);
  }, []);

  return { epctURL, oid, loadingError };
}
